import Search16 from '@carbon/icons-react/es/search/16';
import { reject } from 'lodash';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import styled from 'styled-components';
import { Project } from 'api/project';
import { Option, Select } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { ProjectOption } from 'components/organisms/timesheets/common/project-option';
import { useTranslate } from 'hooks/use-translation';
import { NON_PROJECT_ACTIVITY_ID } from '../../utils';
import { CellEditorParams } from '../types';

const StyledSelect = styled(Select)`
  padding: 0 !important;
  margin-bottom: var(--spacing-03);

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;
const Wrapper = styled.div`
  width: 296px;
  margin-top: var(--spacing-05);
  min-height: 350px;
  width: 272px;
  display: flex;
  flex-direction: column;
`;

const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: '0 var(--spacing-05)',
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: '14px',
    boxShadow: 'none',
    margin: 'var(--spacing-03) 0 var(--spacing-03)',
  }),
  menuItem: (provided) => ({
    ...provided,
    padding: '0 var(--spacing-03)',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'flex !important',
  }),
};

interface OptionEditorProp extends CellEditorParams {
  projectOptions: Option<Project>[];
}

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <Search16 />
  </components.DropdownIndicator>
);

const byNonProject = (option) => option.value === NON_PROJECT_ACTIVITY_ID;

export const ProjectEditor = forwardRef<unknown, OptionEditorProp>(
  ({ value: propValues, projectOptions, api, node, data, onChange }, ref) => {
    const [value, setValue] = useState(propValues);
    const translate = useTranslate();

    useImperativeHandle(ref, () => {
      return {
        getValue: () => {
          return value;
        },
        isPopup: () => true,
        getPopupPosition: () => 'under',
      };
    });
    const [nonProject] = projectOptions.filter(byNonProject);
    const otherProjects = reject(projectOptions, byNonProject);
    const { suggestedProjectIds = [], myProjectIds = [] } = data;
    const predicateBySuggestedProject = (project: Option<Project>): boolean =>
      suggestedProjectIds.includes(project.value as number);
    const predicateByMyProject = (project: Option<Project>): boolean =>
      myProjectIds.includes(project.value as number);

    const projectOption = {
      id: 'projects',
      label: translate('other_project'),
      options: otherProjects.filter(
        (project) =>
          !predicateBySuggestedProject(project) &&
          !predicateByMyProject(project),
      ),
    };
    const nonProjectOption = {
      id: 'nonProject',
      options: [nonProject],
    };

    const suggestedProject = {
      id: 'suggestedProject',
      label: translate('suggested_project'),
      options: otherProjects.filter(predicateBySuggestedProject),
    };

    const yourProject = {
      id: 'yourProject',
      label: translate('your_project'),
      options: otherProjects.filter(
        (project) =>
          predicateByMyProject(project) &&
          !predicateBySuggestedProject(project),
      ),
    };
    const canAutoPopulateSuggestion = suggestedProject.options.length === 1;

    useEffect(() => {
      if (!value) {
        return;
      }
      if (value === propValues) {
        return;
      }
      onChange?.({ api, node, newValue: value, data });
    }, [
      api,
      canAutoPopulateSuggestion,
      data,
      node,
      onChange,
      propValues,
      suggestedProject.options,
      value,
    ]);
    const options = [
      nonProjectOption,
      suggestedProject,
      yourProject,
      projectOption,
    ];

    return (
      <Wrapper>
        <StyledSelect
          type="user"
          options={options}
          components={{
            Option: ProjectOption,
            MenuList: VirtualizedMenuList,
            SingleValue: components.SingleValue,
            DropdownIndicator,
            ClearIndicator: null,
            IndicatorSeparator: null,
          }}
          menuIsOpen
          value={value?.id}
          onChange={setValue}
          grouped
          styles={selectStyles}
          noOptionsMessage={() => (
            <FormattedMessage id="projects.no_results.empty.title" />
          )}
          autoFocus
        />
      </Wrapper>
    );
  },
);
